import { Users, AlertTriangle, Info, Wallet, Check } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import { cn, formatPrice } from '@/lib/utils';
import type { Currency } from '@/lib/utils/constants';

// ============ Types ============

interface NotificationPricingCardProps {
  audienceCount: number;
  pricePerThousand: number;
  calculatedCost: number | null;
  currency: Currency;
  isLoading?: boolean;
  isBelowMinimum?: boolean;
  minBudget?: number;
  audienceLabel?: string;
}

interface PerDayPricingCardProps {
  pricePerDay: number;
  days: number;
  calculatedCost: number | null;
  currency: Currency;
}

interface PerWeekPricingCardProps {
  pricePerWeek: number;
  weeks: number;
  calculatedCost: number | null;
  currency: Currency;
}

interface WalletBalanceCardProps {
  balance: number;
  requiredAmount: number;
  currency: Currency;
  isLoading?: boolean;
}

// ============ Notification Pricing Card ============

export function NotificationPricingCard({
  audienceCount,
  pricePerThousand,
  calculatedCost,
  currency,
  isLoading = false,
  isBelowMinimum = false,
  minBudget = 0,
  audienceLabel = 'Vos abonnés',
}: NotificationPricingCardProps) {
  const isMinimumReached = !isBelowMinimum && audienceCount > 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          📊 Calcul du coût
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Audience row */}
        <div className="flex items-center justify-between py-2 border-b">
          <span className="text-muted-foreground">Votre audience</span>
          {isLoading ? (
            <Skeleton className="h-6 w-24" />
          ) : (
            <Badge variant="secondary">
              <Users className="size-3 mr-1" />
              {audienceCount.toLocaleString()} {audienceLabel.toLowerCase().replace('vos ', '')}
            </Badge>
          )}
        </div>

        {/* Rate row */}
        <div className="flex items-center justify-between py-2 border-b">
          <span className="text-muted-foreground">Tarif</span>
          <span className="text-sm">{formatPrice(pricePerThousand, currency)} / 1000 notif.</span>
        </div>

        {/* Total row */}
        <div className="flex items-center justify-between py-4">
          <span className="font-semibold">Coût total</span>
          {isLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : (
            <span className="text-2xl font-bold text-primary">
              {formatPrice(calculatedCost ?? 0, currency)}
            </span>
          )}
        </div>

        {/* Status indicators */}
        {!isLoading && (
          <>
            {isMinimumReached && (
              <div className="flex items-center gap-2 text-green-600 bg-green-50 dark:bg-green-950 p-3 rounded-lg border border-green-200 dark:border-green-800">
                <Check className="size-4 shrink-0" />
                <span className="font-medium text-sm">Budget minimum atteint</span>
              </div>
            )}

            {isBelowMinimum && (
              <div className="flex items-start gap-2 text-amber-600 bg-amber-50 dark:bg-amber-950 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="size-4 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">Budget minimum: {formatPrice(minBudget, currency)}</p>
                  <p className="text-amber-500 dark:text-amber-400 mt-1">
                    Vous devez avoir plus d'abonnés pour lancer cette campagne.
                  </p>
                </div>
              </div>
            )}

            {audienceCount === 0 && !isBelowMinimum && (
              <div className="flex items-center gap-2 text-amber-600 bg-amber-50 dark:bg-amber-950 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
                <Info className="size-4 shrink-0" />
                <span className="text-sm">
                  Vous n'avez pas encore d'abonnés. Invitez vos clients à s'abonner !
                </span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ============ Per-Day Pricing Card ============

export function PerDayPricingCard({
  pricePerDay,
  days,
  calculatedCost,
  currency,
}: PerDayPricingCardProps) {
  return (
    <Card className="border-primary bg-primary/5">
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Prix par jour</span>
            <span className="font-medium">
              {formatPrice(pricePerDay, currency)}
            </span>
          </div>

          {days > 0 && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Durée</span>
                <span>{days} jours</span>
              </div>

              <div className="border-t" />

              <div className="flex items-center justify-between">
                <span className="font-medium">Coût total</span>
                <span className="text-2xl font-bold text-primary">
                  {formatPrice(calculatedCost ?? 0, currency)}
                </span>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============ Per-Week Pricing Card ============

export function PerWeekPricingCard({
  pricePerWeek,
  weeks,
  calculatedCost,
  currency,
}: PerWeekPricingCardProps) {
  return (
    <Card className="border-primary bg-primary/5">
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Prix par semaine</span>
            <span className="font-medium">
              {formatPrice(pricePerWeek, currency)}
            </span>
          </div>

          {weeks > 0 && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Durée</span>
                <span>
                  {weeks} semaine{weeks > 1 ? 's' : ''}
                </span>
              </div>

              <div className="border-t" />

              <div className="flex items-center justify-between">
                <span className="font-medium">Coût total</span>
                <span className="text-2xl font-bold text-primary">
                  {formatPrice(calculatedCost ?? 0, currency)}
                </span>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============ Wallet Balance Card ============

export function WalletBalanceCard({
  balance,
  requiredAmount,
  currency,
  isLoading = false,
}: WalletBalanceCardProps) {
  const hasInsufficientBalance = !isLoading && balance < requiredAmount;
  const missingAmount = hasInsufficientBalance ? requiredAmount - balance : 0;

  return (
    <Card className={hasInsufficientBalance ? 'border-destructive' : ''}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="size-5 text-muted-foreground" />
            <div>
              <p className="font-medium">Votre solde publicitaire</p>
              {isLoading ? (
                <Skeleton className="h-4 w-24 mt-1" />
              ) : (
                <p className="text-sm text-muted-foreground">
                  Disponible pour vos campagnes
                </p>
              )}
            </div>
          </div>
          {isLoading ? (
            <Skeleton className="h-8 w-20" />
          ) : (
            <span
              className={cn(
                'text-2xl font-bold',
                hasInsufficientBalance ? 'text-destructive' : 'text-green-600'
              )}
            >
              {formatPrice(balance, currency)}
            </span>
          )}
        </div>

        {/* Insufficient balance warning */}
        {hasInsufficientBalance && (
          <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <div className="flex items-start gap-2">
              <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-destructive">Solde insuffisant</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Il vous manque{' '}
                  <span className="font-medium text-destructive">
                    {formatPrice(missingAmount, currency)}
                  </span>{' '}
                  pour lancer cette campagne.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Sufficient balance indicator */}
        {!hasInsufficientBalance && !isLoading && (
          <div className="mt-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
            <div className="flex items-center gap-2 text-green-600">
              <Check className="size-5" />
              <p className="font-medium">Solde suffisant pour cette campagne</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
